import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getString, getLocale } from '../../../locale'
import {
    getSyrupList,
    createSyrup,
    updateSyrup,
    deleteSyrup,
    SyrupInfo,
    SyrupListResponse,
} from '../../../api/0x0005/drinks'
import { getChannelList, updateChannel, ChannelInfo } from '../../../api/0x0005/channels'

type Props = {
    clientId: string
}

type State = {
    syrups: SyrupInfo[]
    channels: ChannelInfo[]
    loading: boolean
    showModal: boolean
    modalMode: 'create' | 'edit'
    selectedSyrup?: SyrupInfo
    form: {
        syrupId: number
        name: string
        description: string
        nameHe: string
        descriptionHe: string
        packageSize: number
        linkedChannelNum: number
    }
}

export default class SyrupsTab extends Component<Props, State> {
    state: State = {
        syrups: [],
        channels: [],
        loading: true,
        showModal: false,
        modalMode: 'create',
        selectedSyrup: undefined,
        form: {
            syrupId: 0,
            name: '',
            description: '',
            nameHe: '',
            descriptionHe: '',
            packageSize: 0,
            linkedChannelNum: 0,
        },
    }

    async componentDidMount() {
        await this.loadData()
    }

    loadData = async () => {
        await Promise.all([this.loadSyrupList(), this.loadChannels()])
    }

    loadSyrupList = async () => {
        try {
            this.setState({ loading: true })
            const response = await getSyrupList(this.props.clientId)
            const data = response.data as SyrupListResponse
            if (data.success === 1) {
                this.setState({ syrups: data.syrups })
            } else {
                toast.error(getString('syrups.load_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('syrups.load_error'))
        } finally {
            this.setState({ loading: false })
        }
    }

    loadChannels = async () => {
        try {
            const response = await getChannelList(this.props.clientId)
            if (response.data.success === 1) {
                this.setState({ channels: response.data.channels })
            }
        } catch (error: any) {
            console.error('Failed to load channels:', error)
        }
    }

    findLinkedChannel = (syrupId: number): ChannelInfo | undefined => {
        return this.state.channels.find((channel) => channel.syrupId === syrupId)
    }

    openCreateModal = () => {
        this.setState({
            showModal: true,
            modalMode: 'create',
            selectedSyrup: undefined,
            form: {
                syrupId: 0,
                name: '',
                description: '',
                nameHe: '',
                descriptionHe: '',
                packageSize: 0,
                linkedChannelNum: 0,
            },
        })
    }

    openEditModal = (syrup: SyrupInfo) => {
        const linkedChannel = this.findLinkedChannel(syrup.id)
        this.setState({
            showModal: true,
            modalMode: 'edit',
            selectedSyrup: syrup,
            form: {
                syrupId: syrup.id,
                name: syrup.name,
                description: syrup.description,
                nameHe: syrup.nameHe,
                descriptionHe: syrup.descriptionHe,
                packageSize: syrup.packageSize,
                linkedChannelNum: linkedChannel?.channelNum || 0,
            },
        })
    }

    closeModal = () => {
        this.setState({ showModal: false })
    }

    handleFormChange = (field: string, value: any) => {
        this.setState((prev) => ({
            form: { ...prev.form, [field]: value },
        }))
    }

    getChannelLabel = (channel: ChannelInfo) => {
        const channelName = channel.channelName?.trim()
        if (channelName) {
            return `${getString('channels.channel')} ${channel.channelNum} - ${channelName}`
        }
        return `${getString('channels.channel')} ${channel.channelNum}`
    }

    canSelectChannel = (channel: ChannelInfo, currentSyrupId: number) => {
        return channel.syrupId === 0 || channel.syrupId === currentSyrupId
    }

    relinkSyrupChannels = async (syrupId: number, selectedChannelNum: number) => {
        const { channels } = this.state
        const selectedChannel =
            selectedChannelNum > 0
                ? channels.find((channel) => channel.channelNum === selectedChannelNum)
                : undefined

        if (selectedChannel && !this.canSelectChannel(selectedChannel, syrupId)) {
            throw new Error(
                getLocale() === 'he'
                    ? 'הערוץ שנבחר כבר משויך לסירופ אחר'
                    : 'Selected channel is already linked to another syrup'
            )
        }

        const updates: Promise<any>[] = []

        channels.forEach((channel) => {
            if (channel.syrupId === syrupId && channel.channelNum !== selectedChannelNum) {
                updates.push(
                    updateChannel(this.props.clientId, {
                        channelNum: channel.channelNum,
                        isActive: channel.isActive,
                        mode: channel.mode,
                        pwmDuty: channel.pwmDuty,
                        syrupId: 0,
                        calibrationDurationMs: channel.calibrationDurationMs || 0,
                        channelName: channel.channelName || '',
                    })
                )
            }
        })

        if (selectedChannel && selectedChannel.syrupId !== syrupId) {
            updates.push(
                updateChannel(this.props.clientId, {
                    channelNum: selectedChannel.channelNum,
                    isActive: selectedChannel.isActive,
                    mode: selectedChannel.mode,
                    pwmDuty: selectedChannel.pwmDuty,
                    syrupId,
                    calibrationDurationMs: selectedChannel.calibrationDurationMs || 0,
                    channelName: selectedChannel.channelName || '',
                })
            )
        }

        await Promise.all(updates)
    }

    handleSave = async () => {
        const { form, modalMode } = this.state

        if (!form.name.trim() || !form.nameHe.trim()) {
            toast.error(getString('syrups.required_fields'))
            return
        }

        try {
            let syrupId = form.syrupId
            if (modalMode === 'create') {
                const response = await createSyrup(this.props.clientId, {
                    name: form.name,
                    description: form.description,
                    nameHe: form.nameHe,
                    descriptionHe: form.descriptionHe,
                    packageSize: form.packageSize,
                })

                if (response.data.success === 1) {
                    syrupId = response.data.syrupId
                } else {
                    toast.error(response.data.errorMsg || getString('syrups.create_error'))
                    return
                }
            } else {
                const response = await updateSyrup(this.props.clientId, {
                    syrupId: form.syrupId,
                    name: form.name,
                    description: form.description,
                    nameHe: form.nameHe,
                    descriptionHe: form.descriptionHe,
                    packageSize: form.packageSize,
                })

                if (response.data.success === 1) {
                    syrupId = form.syrupId
                } else {
                    toast.error(response.data.errorMsg || getString('syrups.update_error'))
                    return
                }
            }

            await this.relinkSyrupChannels(syrupId, form.linkedChannelNum)
            toast.success(modalMode === 'create' ? getString('syrups.create_success') : getString('syrups.update_success'))
            this.closeModal()
            await this.loadData()
        } catch (error: any) {
            toast.error(error?.message || getString('syrups.save_error'))
        }
    }

    handleDelete = async (syrupId: number) => {
        if (!window.confirm(getString('syrups.delete_confirm'))) return

        try {
            const response = await deleteSyrup(this.props.clientId, syrupId)
            if (response.data.success === 1) {
                toast.success(getString('syrups.delete_success'))
                await this.loadSyrupList()
            } else {
                toast.error(response.data.errorMsg || getString('syrups.delete_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('syrups.delete_error'))
        }
    }

    render() {
        const { syrups, channels, loading, showModal, modalMode, form } = this.state

        return (
            <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold dark:text-white">{getString('syrups.title')}</h2>
                    <button
                        onClick={this.openCreateModal}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        {getString('syrups.add')}
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-8">{getString('common.loading')}</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {syrups.map((syrup) => {
                            const isHebrew = getLocale() === 'he'
                            const displayName = (isHebrew && syrup.nameHe) ? syrup.nameHe : syrup.name
                            const displayDescription = (isHebrew && syrup.descriptionHe) ? syrup.descriptionHe : syrup.description
                            const linkedChannel = this.findLinkedChannel(syrup.id)
                            const linkedChannelText = linkedChannel
                                ? this.getChannelLabel(linkedChannel)
                                : (isHebrew ? 'ללא ערוץ' : 'None')
                            
                            return (
                            <div key={syrup.id} className="border dark:border-gray-700 rounded-lg p-4 shadow hover:shadow-md dark:bg-gray-800">
                                <h3 className="text-lg font-semibold mb-2 dark:text-white">{displayName}</h3>
                                <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">{displayDescription}</p>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                    {getString('syrups.package_size')}: {syrup.packageSize} ml
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                    {getString('channels.channel')}: {linkedChannelText}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => this.openEditModal(syrup)}
                                        className="flex-1 px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600"
                                    >
                                        {getString('common.edit')}
                                    </button>
                                    <button
                                        onClick={() => this.handleDelete(syrup.id)}
                                        className="flex-1 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                                    >
                                        {getString('common.delete')}
                                    </button>
                                </div>
                            </div>
                            )
                        })}
                        {syrups.length === 0 && (
                            <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                                {getString('syrups.empty')}
                            </div>
                        )}
                    </div>
                )}

                {/* Modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <h3 className="text-xl font-bold mb-4 dark:text-white">
                                {modalMode === 'create' ? getString('syrups.add') : getString('syrups.edit')}
                            </h3>

                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1 dark:text-white">
                                            {getString('syrups.name_en')} *
                                        </label>
                                        <input
                                            type="text"
                                            value={form.name}
                                            onChange={(e) => this.handleFormChange('name', e.target.value)}
                                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                            maxLength={100}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1 dark:text-white">
                                            {getString('syrups.name_he')} *
                                        </label>
                                        <input
                                            type="text"
                                            value={form.nameHe}
                                            onChange={(e) => this.handleFormChange('nameHe', e.target.value)}
                                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                            dir="rtl"
                                            maxLength={100}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1 dark:text-white">
                                            {getString('syrups.description_en')}
                                        </label>
                                        <textarea
                                            value={form.description}
                                            onChange={(e) => this.handleFormChange('description', e.target.value)}
                                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                            rows={3}
                                            maxLength={500}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1 dark:text-white">
                                            {getString('syrups.description_he')}
                                        </label>
                                        <textarea
                                            value={form.descriptionHe}
                                            onChange={(e) => this.handleFormChange('descriptionHe', e.target.value)}
                                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                            rows={3}
                                            dir="rtl"
                                            maxLength={500}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1 dark:text-white">
                                        {getString('syrups.package_size')}
                                    </label>
                                    <input
                                        type="number"
                                        value={form.packageSize}
                                        onChange={(e) =>
                                            this.handleFormChange('packageSize', parseFloat(e.target.value) || 0)
                                        }
                                        className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                        min={0}
                                        step={0.1}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1 dark:text-white">
                                        {getLocale() === 'he' ? 'ערוץ מקושר' : 'Linked channel'}
                                    </label>
                                    <select
                                        value={form.linkedChannelNum}
                                        onChange={(e) =>
                                            this.handleFormChange('linkedChannelNum', parseInt(e.target.value, 10) || 0)
                                        }
                                        className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                    >
                                        <option value={0}>{getLocale() === 'he' ? 'ללא ערוץ' : 'None'}</option>
                                        {channels.map((channel) => {
                                            const currentSyrupId = modalMode === 'edit' ? form.syrupId : 0
                                            const disabled = !this.canSelectChannel(channel, currentSyrupId)
                                            const suffix =
                                                channel.syrupId !== 0 && disabled
                                                    ? ` (${getLocale() === 'he' ? 'בשימוש' : 'In use'})`
                                                    : ''
                                            return (
                                                <option
                                                    key={channel.channelNum}
                                                    value={channel.channelNum}
                                                    disabled={disabled}
                                                >
                                                    {this.getChannelLabel(channel)}
                                                    {suffix}
                                                </option>
                                            )
                                        })}
                                    </select>
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 mt-6">
                                <button
                                    onClick={this.closeModal}
                                    className="px-4 py-2 border dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                                >
                                    {getString('common.cancel')}
                                </button>
                                <button
                                    onClick={this.handleSave}
                                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                >
                                    {getString('common.save')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }
}
